import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Save, X, Eye, EyeOff, FileText, Search, Sparkles, Wand2, ImageIcon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Article {
  id: string;
  title: string;
  preview: string | null;
  content: any[] | null;
  tags: string[] | null;
  featured_image_url: string | null;
  featured_image_alt: string | null;
  published: boolean | null;
  author: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface ArticleFormData {
  title: string;
  preview: string;
  content: string;
  tags: string;
  featured_image_url: string;
  featured_image_alt: string;
  published: boolean;
  author: string;
}

const emptyFormData: ArticleFormData = {
  title: '',
  preview: '',
  content: '',
  tags: '',
  featured_image_url: '',
  featured_image_alt: '',
  published: true,
  author: 'Klartech Müritz',
};

export function BlogManagement() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [formData, setFormData] = useState<ArticleFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // AI States
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiImageGenerating, setAiImageGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiImageStyle, setAiImageStyle] = useState('illustration');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_articles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setArticles((data || []) as any);
    } catch (error: any) {
      console.error('Error loading articles:', error);
      toast({
        title: 'Fehler beim Laden',
        description: 'Artikel konnten nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setEditingArticle(null);
    setFormData(emptyFormData);
    setIsDialogOpen(true);
  };

  const openEditDialog = (article: Article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title || '',
      preview: article.preview || '',
      content: article.content ? JSON.stringify(article.content, null, 2) : '',
      tags: article.tags?.join(', ') || '',
      featured_image_url: article.featured_image_url || '',
      featured_image_alt: article.featured_image_alt || '',
      published: article.published ?? true,
      author: article.author || 'Klartech Müritz',
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Titel erforderlich',
        description: 'Bitte geben Sie einen Titel ein.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      let contentArray: any[] = [];
      if (formData.content.trim()) {
        try {
          contentArray = JSON.parse(formData.content);
        } catch {
          // If not valid JSON, create simple paragraph content
          contentArray = [{ type: 'paragraph', content: formData.content }];
        }
      }

      const articleData = {
        title: formData.title,
        preview: formData.preview || null,
        content: contentArray,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        featured_image_url: formData.featured_image_url || null,
        featured_image_alt: formData.featured_image_alt || null,
        published: formData.published,
        author: formData.author || 'Klartech Müritz',
        updated_at: new Date().toISOString(),
      };

      if (editingArticle) {
        const { error } = await supabase
          .from('blog_articles')
          .update(articleData)
          .eq('id', editingArticle.id);

        if (error) throw error;
        toast({
          title: 'Artikel aktualisiert',
          description: 'Der Artikel wurde erfolgreich gespeichert.',
        });
      } else {
        const { error } = await supabase
          .from('blog_articles')
          .insert(articleData);

        if (error) throw error;
        toast({
          title: 'Artikel erstellt',
          description: 'Der neue Artikel wurde erfolgreich erstellt.',
        });
      }

      setIsDialogOpen(false);
      loadArticles();
    } catch (error: any) {
      console.error('Error saving article:', error);
      toast({
        title: 'Fehler beim Speichern',
        description: error.message || 'Der Artikel konnte nicht gespeichert werden.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (article: Article) => {
    if (!confirm(`Möchten Sie "${article.title}" wirklich löschen?`)) return;

    try {
      const { error } = await supabase
        .from('blog_articles')
        .delete()
        .eq('id', article.id);

      if (error) throw error;
      toast({
        title: 'Artikel gelöscht',
        description: 'Der Artikel wurde erfolgreich gelöscht.',
      });
      loadArticles();
    } catch (error: any) {
      console.error('Error deleting article:', error);
      toast({
        title: 'Fehler beim Löschen',
        description: error.message || 'Der Artikel konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    }
  };

  const togglePublished = async (article: Article) => {
    try {
      const { error } = await supabase
        .from('blog_articles')
        .update({ published: !article.published })
        .eq('id', article.id);

      if (error) throw error;
      toast({
        title: article.published ? 'Artikel versteckt' : 'Artikel veröffentlicht',
      });
      loadArticles();
    } catch (error: any) {
      console.error('Error toggling publish status:', error);
      toast({
        title: 'Fehler',
        description: 'Status konnte nicht geändert werden.',
        variant: 'destructive',
      });
    }
  };

  // AI Content Generation
  const generateAiContent = async (type: 'full_article' | 'introduction' | 'tips' | 'conclusion' | 'preview') => {
    const topic = aiTopic || formData.title;
    if (!topic) {
      toast({
        title: 'Thema erforderlich',
        description: 'Bitte geben Sie ein Thema oder Titel ein.',
        variant: 'destructive',
      });
      return;
    }

    setAiGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-blog-content', {
        body: {
          type,
          topic,
          keywords: aiKeywords,
          existingContent: formData.content,
          tone: 'friendly',
        },
      });

      if (error) throw error;

      if (data?.content) {
        if (type === 'preview') {
          setFormData({ ...formData, preview: data.content.substring(0, 160) });
        } else if (type === 'full_article') {
          // Convert to JSON content blocks
          const blocks = data.content.split('\n\n').map((paragraph: string) => {
            if (paragraph.startsWith('# ')) {
              return { type: 'h1', content: paragraph.replace('# ', '') };
            } else if (paragraph.startsWith('## ')) {
              return { type: 'h2', content: paragraph.replace('## ', '') };
            } else if (paragraph.startsWith('### ')) {
              return { type: 'h3', content: paragraph.replace('### ', '') };
            } else if (paragraph.startsWith('• ') || paragraph.startsWith('- ')) {
              return { type: 'bullet', content: paragraph.replace(/^[•-] /, '') };
            }
            return { type: 'paragraph', content: paragraph };
          }).filter((b: any) => b.content.trim());
          setFormData({ ...formData, content: JSON.stringify(blocks, null, 2) });
        } else {
          // Append to existing content
          const existingContent = formData.content ? formData.content + '\n\n' : '';
          setFormData({ ...formData, content: existingContent + data.content });
        }
        toast({ title: 'KI-Inhalt generiert!' });
      }
    } catch (error: any) {
      console.error('AI generation error:', error);
      toast({
        title: 'Fehler bei KI-Generierung',
        description: error.message || 'Bitte versuchen Sie es erneut.',
        variant: 'destructive',
      });
    } finally {
      setAiGenerating(false);
    }
  };

  // AI Image Generation
  const generateAiImage = async () => {
    const topic = aiTopic || formData.title;
    if (!topic) {
      toast({
        title: 'Thema erforderlich',
        description: 'Bitte geben Sie ein Thema oder Titel ein.',
        variant: 'destructive',
      });
      return;
    }

    setAiImageGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-blog-image', {
        body: {
          articleTitle: formData.title || topic,
          articleTopic: topic,
          style: aiImageStyle,
        },
      });

      if (error) throw error;

      if (data?.imageUrl) {
        setFormData({ 
          ...formData, 
          featured_image_url: data.imageUrl,
          featured_image_alt: `${topic} - Klartech Müritz Blog`
        });
        toast({ title: 'KI-Bild generiert!' });
      }
    } catch (error: any) {
      console.error('AI image generation error:', error);
      toast({
        title: 'Fehler bei Bildgenerierung',
        description: error.message || 'Bitte versuchen Sie es erneut.',
        variant: 'destructive',
      });
    } finally {
      setAiImageGenerating(false);
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Blog-Artikel
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={openNewDialog} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Neuer Artikel
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredArticles.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Keine Artikel gefunden</p>
              <p className="text-sm">Erstellen Sie Ihren ersten Blog-Artikel.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredArticles.map((article) => (
                <div
                  key={article.id}
                  className="p-4 border rounded-xl bg-white hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">{article.title}</span>
                        {article.published ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            Veröffentlicht
                          </span>
                        ) : (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <EyeOff className="w-3 h-3" />
                            Entwurf
                          </span>
                        )}
                      </div>
                      {article.preview && (
                        <p className="text-sm text-slate-600 line-clamp-2">{article.preview}</p>
                      )}
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {article.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                          {article.tags.length > 3 && (
                            <span className="text-xs text-slate-400">+{article.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {formatDate(article.created_at)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePublished(article)}
                        title={article.published ? 'Verstecken' : 'Veröffentlichen'}
                      >
                        {article.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(article)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(article)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Article Editor Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) setShowAiPanel(false);
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                {editingArticle ? 'Artikel bearbeiten' : 'Neuer Artikel'}
              </DialogTitle>
              <Button
                variant={showAiPanel ? "default" : "outline"}
                size="sm"
                onClick={() => setShowAiPanel(!showAiPanel)}
                className={showAiPanel ? "bg-purple-600 hover:bg-purple-700" : "border-purple-300 text-purple-600 hover:bg-purple-50"}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                KI-Assistent
              </Button>
            </div>
          </DialogHeader>

          <div className="flex gap-6">
            {/* Main Form */}
            <div className={`space-y-4 py-4 ${showAiPanel ? 'w-2/3' : 'w-full'} transition-all`}>
              <div className="space-y-2">
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Artikel-Titel"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="preview">Vorschau / Kurzbeschreibung</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => generateAiContent('preview')}
                    disabled={aiGenerating || !formData.content}
                    className="text-purple-600 hover:text-purple-700 h-6 px-2"
                  >
                    {aiGenerating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wand2 className="w-3 h-3 mr-1" />}
                    KI generieren
                  </Button>
                </div>
                <Textarea
                  id="preview"
                  value={formData.preview}
                  onChange={(e) => setFormData({ ...formData, preview: e.target.value })}
                  placeholder="Kurze Beschreibung für die Vorschau..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Inhalt (JSON-Format oder Text)</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder='[{"type": "paragraph", "content": "Ihr Text hier..."}]'
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Entweder JSON-Array mit type/content Objekten oder einfacher Text
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (kommagetrennt)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="Marketing, Social Media, Tipps"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author">Autor</Label>
                  <Input
                    id="author"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="Klartech Müritz"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="featured_image_url">Bild-URL</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={generateAiImage}
                      disabled={aiImageGenerating || (!formData.title && !aiTopic)}
                      className="text-purple-600 hover:text-purple-700 h-6 px-2"
                    >
                      {aiImageGenerating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                      KI-Bild
                    </Button>
                  </div>
                  <Input
                    id="featured_image_url"
                    value={formData.featured_image_url}
                    onChange={(e) => setFormData({ ...formData, featured_image_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="featured_image_alt">Bild Alt-Text</Label>
                  <Input
                    id="featured_image_alt"
                    value={formData.featured_image_alt}
                    onChange={(e) => setFormData({ ...formData, featured_image_alt: e.target.value })}
                    placeholder="Beschreibung des Bildes"
                  />
                </div>
              </div>

              {formData.featured_image_url && (
                <div className="relative">
                  <img 
                    src={formData.featured_image_url} 
                    alt={formData.featured_image_alt || 'Vorschau'} 
                    className="w-full h-32 object-cover rounded-lg border"
                    onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                  />
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Switch
                  id="published"
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
                />
                <Label htmlFor="published">Veröffentlicht</Label>
              </div>
            </div>

            {/* AI Panel */}
            {showAiPanel && (
              <div className="w-1/3 py-4 space-y-4">
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-200 p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-900">KI-Textassistent</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-purple-700">Thema/Titel</Label>
                      <Input
                        value={aiTopic}
                        onChange={(e) => setAiTopic(e.target.value)}
                        placeholder={formData.title || "z.B. Windows 11 Tipps"}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs text-purple-700">Keywords (optional)</Label>
                      <Input
                        value={aiKeywords}
                        onChange={(e) => setAiKeywords(e.target.value)}
                        placeholder="Sicherheit, Senioren, einfach"
                        className="mt-1"
                      />
                    </div>

                    <div className="space-y-2 pt-2">
                      <Button
                        onClick={() => generateAiContent('full_article')}
                        disabled={aiGenerating}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        size="sm"
                      >
                        {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                        Kompletten Artikel generieren
                      </Button>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateAiContent('introduction')}
                          disabled={aiGenerating}
                          className="text-purple-600 border-purple-200"
                        >
                          Einleitung
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateAiContent('tips')}
                          disabled={aiGenerating}
                          className="text-purple-600 border-purple-200"
                        >
                          Tipps
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateAiContent('conclusion')}
                          disabled={aiGenerating}
                          className="text-purple-600 border-purple-200"
                        >
                          Fazit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateAiContent('preview')}
                          disabled={aiGenerating}
                          className="text-purple-600 border-purple-200"
                        >
                          Vorschau
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg border border-cyan-200 p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <ImageIcon className="w-5 h-5 text-cyan-600" />
                    <h4 className="font-semibold text-cyan-900">KI-Bildgenerierung</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-cyan-700">Bildstil</Label>
                      <Select value={aiImageStyle} onValueChange={setAiImageStyle}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="illustration">Illustration</SelectItem>
                          <SelectItem value="tech">Tech-fokussiert</SelectItem>
                          <SelectItem value="friendly">Freundlich</SelectItem>
                          <SelectItem value="photo">Fotorealistisch</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button
                      onClick={generateAiImage}
                      disabled={aiImageGenerating || (!formData.title && !aiTopic)}
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                      size="sm"
                    >
                      {aiImageGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                      Header-Bild generieren
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
